import React, { useState } from "react";
import { Modal, Button, Typography, Spin } from "antd";
import { EnvironmentOutlined, CompassOutlined, AimOutlined } from "@ant-design/icons";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import MapGoogle from "../MapGoogle/MapGoogle";
import blizuLogo from "../../assets/logo.png";

const { Text, Paragraph } = Typography;

const ModalUbicacion = ({ visible, onOk, onCancel, permitirCerrar = false }) => {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [locationStatus, setLocationStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Cada vez que se abre arranca limpio (sin marcador ni errores de la vez anterior)
  React.useEffect(() => {
    if (visible) {
      setMarkerPosition(null);
      setLocationStatus("idle");
      setErrorMessage(null);
    }
  }, [visible]);

  const requestLocation = async () => {
    setLocationStatus("loading");

    try {
      // Pide el permiso de forma explícita en dispositivos nativos
      if (Capacitor.isNativePlatform()) {
        const permisos = await Geolocation.checkPermissions();
        if (permisos.location !== "granted") {
          const resultado = await Geolocation.requestPermissions();
          if (resultado.location !== "granted") throw new Error("denied");
        }
      }

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
      });

      setMarkerPosition({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setLocationStatus("success");
    } catch (error) {
      console.error("Error de ubicación:", error);
      const texto = String(error?.message || "").toLowerCase();

      // Nunca se inventa una ubicación: si falla, el usuario la marca a mano en el mapa.
      if (texto.includes("denied") || texto.includes("permission")) {
        setErrorMessage("Permiso denegado. Activa la ubicación para Blizu en los ajustes, o márcala en el mapa.");
      } else if (texto.includes("disabled") || texto.includes("unavailable")) {
        setErrorMessage("El GPS está apagado. Enciéndelo o márcala en el mapa.");
      } else if (texto.includes("timeout") || texto.includes("timed out")) {
        setErrorMessage("Tardó demasiado en ubicarte. Inténtalo de nuevo o márcala en el mapa.");
      } else {
        setErrorMessage("No pudimos obtener tu ubicación. Inténtalo de nuevo o márcala en el mapa.");
      }
      setLocationStatus("error");
    }
  };

  // Entrar al mapa sin marcador: el botón de confirmar sigue deshabilitado hasta que toquen el mapa
  const elegirEnMapa = () => {
    setMarkerPosition(null);
    setLocationStatus("success");
  };

  const handleMapClick = ({ latLng }) => {
    setMarkerPosition({
      lat: latLng.lat(),
      lng: latLng.lng(),
    });
  };

  const renderRequestView = () => (
    <div style={{ textAlign: "center", padding: "0 4px 4px" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#f6ffed",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 12,
        }}
      >
        <EnvironmentOutlined style={{ fontSize: 32, color: "#52c41a" }} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6, color: "#1f1f1f" }}>
        Activa tu ubicación
      </div>
      <Paragraph style={{ color: "#8c8c8c", marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
        Para mostrarte qué venden cerca tuyo, necesitamos saber dónde estás.
      </Paragraph>
      <Button
        type="primary"
        size="large"
        block
        onClick={requestLocation}
        loading={locationStatus === "loading"}
        icon={<CompassOutlined />}
        style={{ height: 48, fontSize: 15, fontWeight: 500 }}
      >
        {locationStatus === "loading" ? "Obteniendo ubicación..." : "Habilitar ubicación"}
      </Button>
    </div>
  );

  const renderLoadingView = () => (
    <div style={{ textAlign: "center", padding: "32px 4px" }}>
      <Spin size="large" />
      <div style={{ marginTop: 16, color: "#595959", fontSize: 15 }}>
        Detectando tu ubicación...
      </div>
    </div>
  );

  const renderErrorView = () => (
    <div
      style={{
        textAlign: "center",
        padding: "8px 4px 4px",
        background: "#fffbe6",
        borderRadius: 10,
        border: "1px solid #ffe58f",
        marginTop: 8,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#fff1b8",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 12,
        }}
      >
        <AimOutlined style={{ fontSize: 32, color: "#d48806" }} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: "#874d00" }}>
        No pudimos ubicarte
      </div>
      <Paragraph style={{ color: "#874d00", marginBottom: 20, fontSize: 14 }}>
        {errorMessage}
      </Paragraph>
      <Button type="primary" onClick={requestLocation} block size="large" style={{ height: 44 }}>
        Intentar de nuevo
      </Button>
      <Button onClick={elegirEnMapa} block size="large" style={{ height: 44, marginTop: 8 }}>
        Marcar en el mapa
      </Button>
    </div>
  );

  const renderMapView = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 10 : 14 }}>
      <div
        style={{
          background: "#e6f4ff",
          padding: isMobile ? "10px 12px" : "12px 14px",
          borderRadius: 10,
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <AimOutlined style={{ color: "#1890ff", fontSize: 18, marginTop: 2 }} />
        <div>
          <Text strong style={{ color: "#003eb3" }}>
            Ajusta tu punto de entrega
          </Text>
          <Paragraph style={{ marginBottom: 0, fontSize: 13, color: "#003eb3" }}>
            Toca el mapa para colocar el marcador en la dirección exacta.
          </Paragraph>
        </div>
      </div>
      <div
        style={{
          height: isMobile ? 320 : 400,
          width: "100%",
          borderRadius: 12,
          overflow: "hidden",
          border: "1px solid #f0f0f0",
        }}
      >
        <MapGoogle onMapClick={handleMapClick} markerPosition={markerPosition} />
      </div>
    </div>
  );

  let content;
  if (locationStatus === "loading" && !markerPosition) content = renderLoadingView();
  else if (locationStatus === "success") content = renderMapView();
  else if (locationStatus === "error") content = renderErrorView();
  else content = renderRequestView();

  const showConfirmButton = locationStatus === "success" && markerPosition;

  return (
    <Modal
      open={visible}
      onOk={() => onOk(markerPosition)}
      okText="Confirmar ubicación"
      onCancel={onCancel}
      cancelButtonProps={{ style: { display: "none" } }}
      cancelText={null}
      okButtonProps={{ disabled: !markerPosition, size: "large", style: { height: 44 } }}
      maskClosable={permitirCerrar}
      closable={permitirCerrar}
      centered
      width={isMobile ? "100%" : 620}
      style={{ top: isMobile ? 12 : undefined }}
      footer={
        showConfirmButton
          ? [
              <Button
                key="ok"
                type="primary"
                size="large"
                disabled={!markerPosition}
                onClick={() => onOk(markerPosition)}
                style={{ height: 44, width: "100%", fontWeight: 500 }}
              >
                Confirmar ubicación
              </Button>,
            ]
          : null
      }
      title={null}
      styles={{
        body: { padding: isMobile ? "16px 16px 14px" : "24px 24px 20px" },
        footer: { padding: isMobile ? "0 16px 16px" : "0 24px 20px", marginTop: 0 },
      }}
    >
      <div style={{ textAlign: "center", marginBottom: isMobile ? 10 : 16 }}>
        <img
          src={blizuLogo}
          alt="Blizu"
          style={{ width: isMobile ? 72 : 88, height: "auto", marginBottom: isMobile ? 8 : 12 }}
        />
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 1.2,
            color: "#8c8c8c",
            textTransform: "uppercase",
            display: "block",
          }}
        >
          Bienvenido
        </Text>
        <div
          style={{
            fontSize: isMobile ? 18 : 22,
            fontWeight: 700,
            color: "#1f1f1f",
            marginTop: 4,
          }}
        >
          Empezamos por ti
        </div>
      </div>
      {content}
    </Modal>
  );
};

export default ModalUbicacion;